import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Context } from "../../../models/context";
import { Project } from "../../../models/project";
import { GetProjects } from "../../../services/projects";
import { GetDefaults, PostDefaults } from "../../../services/defaults";
import Help from "../../../components/help/Help";
import "./defaultPage.css";
import { useAlerts } from "../../../contexts/AlertContext";

const HINT_TYPES: { label: string; value: string }[] = [
    { label: "Record Attachment", value: "record_attachment" },
    { label: "Possible Duplicate", value: "possible_duplicate" },
    { label: "Partially Attached", value: "partially_attached" }
];

const DIFFICULTY_OPTIONS: { label: string; value: string }[] = [
    { label: "Easy", value: "0" },
    { label: "Medium", value: "0.5" },
    { label: "Hard", value: "1" }
];

const projectsService = new GetProjects();
const defaultsService = new GetDefaults();
const saveDefaultsService = new PostDefaults();

export default function Default() {
    const context = useContext(Context);
    const navigate = useNavigate();
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
    const [selectedHint, setSelectedHint] = useState<string | null>(null);
    const [selectedDifficulty, setSelectedDifficulty] = useState<string | null>(null);
    const [tehEnabled, setTehEnabled] = useState<boolean>(false);
    const [loadingDefaults, setLoadingDefaults] = useState<boolean>(true);
    const [savingDefaults, setSavingDefaults] = useState(false);
    const [saveMessage, setSaveMessage] = useState<string>("");
    const sortedProjects = [...projects].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
    );
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
        projects: false,
        hintTypes: false,
        difficultly: false,
        teh: false,
    });
    const toggle = (key: string) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
    const {addError} = useAlerts();

    useEffect(() => {
        if (!context.user) {
            navigate("/login");
            return;
        }

        setLoadingDefaults(true);
        Promise.all([
            projectsService.request(context.user),
            defaultsService.request(context.user)
        ]).then(([projectResponse, defaults]) => {
            setProjects(projectResponse.map(Project.ensureInstance));
            setSelectedProjects(defaults.projectNames ?? []);
            setSelectedDifficulty(
                DIFFICULTY_OPTIONS.some((d) => d.value === defaults.difficulty) ? defaults.difficulty : null
            );
            setTehEnabled(Boolean(defaults.TEH));
            setSelectedHint(
                HINT_TYPES.some((h) => h.value === defaults.hintType) ? defaults.hintType : null
            );
        }).catch((error) => {
            console.error("Error loading page data:", error);
            addError(error as Error, "Error loading page data");
        }).finally(() => {
            setLoadingDefaults(false);
        });
    }, [context.user, navigate]);

    const toggleItem = (
        value: string,
        selectedItems: string[],
        setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>
    ) => {
        if (selectedItems.includes(value)) {
            setSelectedItems(selectedItems.filter((item) => item !== value));
            return;
        }

        setSelectedItems([...selectedItems, value]);
    };
    const toggleSingleSelect = (
        selectedOption: string,
        setSelectedOption: React.Dispatch<React.SetStateAction<string | null>>
    ) => {
        setSelectedOption((prev) => (prev === selectedOption ? null : selectedOption));
    };
    const handleSaveDefaults = async () => {
        if (!context.user) {
            navigate("/login");
            return;
        }
        if (loadingDefaults) return;
        if (!selectedHint || !selectedDifficulty) {
            setSaveMessage("Select one hint type and one difficulty before saving.");
            return;
        }

        setSavingDefaults(true);
        setSaveMessage("");
        try {
            await saveDefaultsService.request({
                projectNames: selectedProjects,
                hintType: selectedHint,
                difficulty: selectedDifficulty,
                TEH: tehEnabled
            }, context.user);
            setSaveMessage("Defaults saved.");
        } catch (error) {
            addError(error as Error, "Error saving defaults");
            console.error("Error saving defaults:", error);
            setSaveMessage("Unable to save defaults.");
        } finally {
            setSavingDefaults(false);
        }
    };

    return (
        <div id="default-page">




            <div id="default-wrapper" className="container">
                <div className="help-title" id="default-help">
                    <h1>Default Project</h1>
                    <Help title="Default Project" color="var(--green-medium)" className="help-button">
                        <p>This is the Default Project dashboard, where you can adjust the settings.</p>
                        <p>You can select a default project, hint type, difficulty, and toggle tree-extending hints (TEH).</p>
                        <p>Click on the "Save Defaults" button to save your work.</p>
                    </Help>
                </div>

                <div id="default-accordion">
                    <section className="default-section" id="first">
                        <button className="section-toggle" onClick={() => toggle("projects")}>
                            <h2>Projects:</h2>
                            <span>{collapsed.projects ? "▶" : "▼"}</span>
                        </button>
                        {!collapsed.projects && (
                            <div className="options-grid projects-grid">
                                {sortedProjects.map((project) => (
                                    <label key={project.name} className="option-item">
                                        <input
                                            type="checkbox"
                                            checked={selectedProjects.includes(project.name)}
                                            onChange={() => toggleItem(project.name, selectedProjects, setSelectedProjects)}
                                        />
                                        <span>{project.name}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </section>
                </div>

                <section className="default-section">
                    <button className="section-toggle" onClick={() => toggle("hintTypes")}>
                        <h2>Hint Types:</h2>
                        <span>{collapsed.hintTypes ? "▶" : "▼"}</span>
                    </button>
                    {!collapsed.hintTypes && (
                        <div className="options-grid hints-grid">
                            {HINT_TYPES.map((hintType) => (
                                <label key={hintType.value} className="option-item">
                                    <input
                                        type="radio"
                                        checked={selectedHint === hintType.value}
                                        onChange={() => toggleSingleSelect(hintType.value, setSelectedHint)}
                                    />
                                    <span>{hintType.label}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </section>

                <section className="default-section">
                    <button className="section-toggle" onClick={() => toggle("difficulty")}>
                        <h2>Difficulty:</h2>
                        <span>{collapsed.difficulty ? "▶" : "▼"}</span>
                    </button>
                    {!collapsed.difficulty && (
                        <div className="options-grid difficulty-grid">
                            {DIFFICULTY_OPTIONS.map((difficultyOption) => (
                                <label key={difficultyOption.value} className="option-item">
                                    <input
                                        type="radio"
                                        checked={selectedDifficulty === difficultyOption.value}
                                        onChange={() => toggleSingleSelect(difficultyOption.value, setSelectedDifficulty)}
                                    />
                                    <span>{difficultyOption.label}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </section>

                <section className="default-section">
                    <button className="section-toggle" onClick={() => toggle("teh")}>
                        <h2>Tree Extending Hints (TEH):</h2>
                        <span>{collapsed.teh ? "▶" : "▼"}</span>
                    </button>
                    {!collapsed.teh && (
                        <label className="toggle-row">
                            <span className="option-item">Enable</span>
                            <span className="switch">
                                <input
                                    type="checkbox"
                                    checked={tehEnabled}
                                    onChange={(event) => setTehEnabled(event.target.checked)}
                                />
                                <span className="slider" />
                            </span>
                        </label>
                    )}
                </section>
                <section className="default-actions">
                    <button
                        type="button"
                        className="button"
                        id="save-defaults-button"
                        onClick={handleSaveDefaults}
                        disabled={savingDefaults || loadingDefaults}
                    >
                        {loadingDefaults ? "Loading defaults..." : savingDefaults ? "Saving..." : "Save Defaults"}
                    </button>

                </section>
            </div>

            {saveMessage !== "" && <p id="save-defaults-message">{saveMessage}</p>}
        </div>
    );
}
